import { axiosClientv1 } from "./apiClient";
import { ApiResponse } from "../Types";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface GatewayStatusResponse {
  status:
    | "INITIALIZING"
    | "QR"
    | "READY"
    | "AUTH_FAILURE"
    | "DISCONNECTED"
    | "OFFLINE";
  qr?: string;
  enabled: boolean;
  connectedAt: string | null;
  uptimeSeconds: number | null;
  totalMessages: number;
}

export interface ActivityLogEntry {
  from: string;
  body: string;
  replied: boolean;
  timestamp: string;
}

export interface ActivityLogsResponse {
  logs: ActivityLogEntry[];
}

// ─── API Functions ────────────────────────────────────────────────────────────

export const GetWhatsAppGatewayStatus = async (
  tenantId: string,
): Promise<GatewayStatusResponse> => {
  try {
    const response = await axiosClientv1.get(`/whatsapp/gateway/status`);
    return (response.data?.data ?? response.data) as GatewayStatusResponse;
  } catch {
    return {
      status: "OFFLINE",
      enabled: false,
      connectedAt: null,
      uptimeSeconds: null,
      totalMessages: 0,
    };
  }
};

export const GetWhatsAppActivityLogs = async (
  tenantId: string,
): Promise<ActivityLogsResponse> => {
  try {
    const response = await axiosClientv1.get(`/whatsapp/gateway/logs`);
    return (response.data?.data ?? response.data) as ActivityLogsResponse;
  } catch {
    return { logs: [] };
  }
};

export const PostWhatsAppLogout = async (
  tenantId: string,
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await axiosClientv1.post(`/whatsapp/gateway/logout`);
    const data = (response.data?.data ?? response.data) as {
      success?: boolean;
      message?: string;
    };
    return {
      success: data.success ?? true,
      message: data.message || "Logout request successful.",
    };
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Logout request failed.",
    };
  }
};

export const PostWhatsAppToggle = async (
  tenantId: string,
  enabled: boolean,
): Promise<{ success: boolean; enabled: boolean }> => {
  try {
    const response = await axiosClientv1.post(`/whatsapp/gateway/toggle`, {
      enabled,
    });
    const data = (response.data?.data ?? response.data) as {
      success?: boolean;
      enabled: boolean;
    };
    return {
      success: data.success ?? true,
      enabled: data.enabled,
    };
  } catch (error: any) {
    return { success: false, enabled };
  }
};

export const SendWhatsAppTextMessage = async (payload: {
  phone: string;
  message: string;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(
      `/whatsapp/send-message`,
      payload,
    );
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to send WhatsApp message.",
      error: {
        code: error?.response?.status || 502,
        type: "server_error",
      },
    };
  }
};

export const SendWhatsAppDocument = async (payload: {
  phone: string;
  document_base64: string;
  filename?: string | null;
  caption?: string | null;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(
      `/whatsapp/send-document`,
      payload,
    );
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to send WhatsApp document.",
      error: {
        code: error?.response?.status || 502,
        type: "server_error",
      },
    };
  }
};

export const GetWhatsAppTenantTokens = async (): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get(`/whatsapp/tokens`);
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to load usage statistics.",
      error: { code: error?.response?.status || 500, type: "server_error" },
    };
  }
};

export const GetWhatsAppTenantTokenUsage = async (): Promise<
  ApiResponse<any>
> => {
  try {
    const response = await axiosClientv1.get(`/whatsapp/tokens/usage`);
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to load token usage logs.",
      error: { code: error?.response?.status || 500, type: "server_error" },
    };
  }
};

export const PostWhatsAppConnect = async (
  tenantId: string,
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await axiosClientv1.post(`/whatsapp/gateway/connect`);
    const data = (response.data?.data ?? response.data) as {
      success?: boolean;
      message?: string;
    };
    return {
      success: data.success ?? true,
      message: data.message || "Connect request successful.",
    };
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Connect request failed.",
    };
  }
};
